import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { SERIES_PALETTE } from './colors';
import { getChartTooltipStyle } from '../../../../../utils/chartDefaults';

ChartJS.register(CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip);

// Fixed teal for ensemble lines, matching the ENS square in the matrix.
const ENSEMBLE_COLOR = '#05788a';
// Forecaster lines are context: dimmed so the ensemble stays readable even
// with 30+ series on screen.
const FORECASTER_ALPHA = 0.35;
// Per-tick readout cap: best N series listed in the tooltip (plus the
// ensemble); the footer reports how many were cut.
const TOOLTIP_MAX_ROWS = 12;
// A forecaster point is comparable to the ensemble only when its sample
// count reaches this fraction of the ensemble's at the same lead hour;
// below it the point renders hollow and the tooltip shows the coverage.
const COVERAGE_THRESHOLD = 0.95;

// #rrggbb -> rgba(r,g,b,a), for the dimmed forecaster lines.
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const isDark = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

// Axis styling mirrors ErrorsOverTimeChart so the two Overview plots read as
// one system (same tick size, text and grid colors).
const TEXT_COLOR = (dark) => (dark ? '#e5e7eb' : '#374151');
const GRID_COLOR = (dark) => (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)');

function identityOf(row, blurSensitive) {
  if (row.kind === 'ensemble') return `Ensemble (${row.id})`;
  if (!blurSensitive && row.name) return row.name;
  return row.username || row.id;
}

/**
 * Error by forecast leadtime: one line per forecaster plus the ensemble,
 * pooled over the selected month and horizon. Live submissions only; the
 * tooltip carries the sample count so sparse coverage is never mistaken
 * for comparable performance.
 */
export default function ErrorByLeadtimeChart({
  data,
  metricLabel = 'RMSE',
  blurSensitive = false,
  headerRight = null,
  colorMap = {},
  traceUserId = null,
  onTrace = null,
  hiddenUserIds = [],
  onHideUser = null,
  onRestoreHidden = null,
}) {
  const dark = isDark();
  const series = useMemo(() => data?.series || [], [data?.series]);
  const traceActive = Boolean(traceUserId);
  const [fullCoverageOnly, setFullCoverageOnly] = useState(false);
  const hiddenIds = useMemo(
    () => new Set(hiddenUserIds.map((id) => String(id))),
    [hiddenUserIds],
  );

  const { chartData, hasData, lowCoverageCount } = useMemo(() => {
    // Coverage baseline: the ensemble's sample count per lead hour (the
    // ensemble is scored on every live day, so it is the densest series).
    const ensNByLead = new Map();
    series.forEach((s) => {
      if (s.kind !== 'ensemble') return;
      (s.points || []).forEach((p) => {
        const cur = ensNByLead.get(p.lead_hours) || 0;
        if (p.n_samples > cur) ensNByLead.set(p.lead_hours, p.n_samples);
      });
    });

    // Forecasters whose overall sample count falls below the threshold
    // fraction of the ensemble's (missing leads count as zero coverage).
    // Counted regardless of the filter state so the toggle can announce
    // how many lines it would remove.
    const lowCoverage = new Set();
    if (ensNByLead.size) {
      series.forEach((s) => {
        if (s.kind === 'ensemble') return;
        const fcByLead = new Map(
          (s.points || []).map((p) => [p.lead_hours, p.n_samples]),
        );
        let fcSum = 0;
        let ensSum = 0;
        ensNByLead.forEach((ref, lead) => {
          ensSum += ref;
          fcSum += fcByLead.get(lead) || 0;
        });
        if (ensSum && fcSum < COVERAGE_THRESHOLD * ensSum) {
          lowCoverage.add(s.id);
        }
      });
    }
    const visibleSeries = series.filter((s) => (
      s.kind === 'ensemble'
      || (!hiddenIds.has(String(s.id)) && (!fullCoverageOnly || !lowCoverage.has(s.id)))
    ));

    const leadSet = new Set();
    visibleSeries.forEach((s) => (s.points || []).forEach((p) => leadSet.add(p.lead_hours)));
    const leads = Array.from(leadSet).sort((a, b) => a - b);
    const leadIndex = new Map(leads.map((l, i) => [l, i]));

    let paletteIdx = 0;
    const datasets = visibleSeries.map((s) => {
      const points = new Array(leads.length).fill(null);
      const meta = new Array(leads.length).fill(null);
      (s.points || []).forEach((p) => {
        const i = leadIndex.get(p.lead_hours);
        points[i] = p.value;
        meta[i] = { ...p, ens_n: ensNByLead.get(p.lead_hours) || null };
      });
      const isEnsemble = s.kind === 'ensemble';
      let color;
      if (isEnsemble) {
        color = ENSEMBLE_COLOR;
      } else if (colorMap[s.id]) {
        color = colorMap[s.id];
      } else {
        color = SERIES_PALETTE[paletteIdx % SERIES_PALETTE.length];
        paletteIdx += 1;
      }
      // Visual hierarchy: forecasters are dimmed context by default; the
      // shared trace spotlights one of them and fades the rest further.
      const traced = !isEnsemble && s.id === traceUserId;
      let alpha = FORECASTER_ALPHA;
      if (traceActive) alpha = traced ? 1 : 0.12;
      const lineColor = isEnsemble ? color : hexToRgba(color, alpha);
      // Comparability cue: hollow points where the forecaster's sample
      // count is below the threshold fraction of the ensemble's.
      const isHollow = (p) => Boolean(
        !isEnsemble && p && p.ens_n
        && p.n_samples < COVERAGE_THRESHOLD * p.ens_n,
      );
      return {
        label: identityOf(s, blurSensitive),
        data: points,
        pointMeta: meta,
        meta: { userId: s.id, isEnsemble },
        borderColor: lineColor,
        backgroundColor: lineColor,
        borderWidth: isEnsemble ? 3 : (traced ? 2.5 : 1),
        // Lower order draws on top: ensemble above the spotlight, spotlight
        // above the dimmed pack (mirrors ErrorsOverTimeChart).
        order: isEnsemble ? 0 : (traced ? 1 : 3),
        pointRadius: meta.map((p) => {
          if (!p) return 0;
          if (isEnsemble) return 3;
          if (traceActive && !traced) return 0;
          if (traced) return isHollow(p) ? 3 : 2.5;
          return isHollow(p) ? 2.5 : 1.5;
        }),
        // Hollow (open) circle marks an under-covered point.
        pointBackgroundColor: meta.map((p) => (
          isHollow(p) ? 'transparent' : lineColor)),
        pointBorderColor: lineColor,
        pointBorderWidth: meta.map((p) => (isHollow(p) ? 1.5 : 1)),
        pointHoverRadius: 4,
        // Easier selection without visually thickening the dense field.
        pointHitRadius: 10,
        spanGaps: true,
        tension: 0.2,
      };
    });

    return {
      chartData: { labels: leads.map((l) => `${l}h`), datasets },
      hasData: leads.length > 0,
      lowCoverageCount: lowCoverage.size,
    };
  }, [series, blurSensitive, colorMap, traceActive, traceUserId, fullCoverageOnly, hiddenIds]);

  const selectedForecaster = useMemo(() => chartData.datasets.find((dataset) => (
    dataset.meta?.userId != null
    && !dataset.meta.isEnsemble
    && String(dataset.meta.userId) === String(traceUserId)
  )) || null, [chartData, traceUserId]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    // Index mode: hovering a lead hour reads out every series at that tick,
    // sorted best-first (capped below so the tooltip fits the canvas).
    interaction: { mode: 'index', intersect: false },
    // Clicking a forecaster line toggles the shared trace, mirroring the
    // matrix and the errors-over-time chart. The ensemble never traces.
    // Index mode hands us every dataset at the tick, so resolve the line
    // nearest to the pointer ourselves.
    onClick: (evt, _elements, chart) => {
      if (!evt?.native) return;
      if (!onTrace) return;
      const els = chart.getElementsAtEventForMode(
        evt.native, 'nearest', { intersect: false }, true,
      );
      if (!els.length) return;
      const m = chartData.datasets[els[0].datasetIndex]?.meta;
      if (!m || m.isEnsemble) return;
      onTrace(m.userId === traceUserId ? null : m.userId);
    },
    onHover: (evt, _elements, chart) => {
      if (!chart?.canvas || !evt?.native) return;
      const els = onTrace ? chart.getElementsAtEventForMode(
        evt.native, 'nearest', { intersect: true }, true,
      ) : [];
      const overForecaster = els.some((el) => {
        const m = chartData.datasets[el.datasetIndex]?.meta;
        return m && !m.isEnsemble;
      });
      chart.canvas.style.cursor = overForecaster ? 'pointer' : 'default';
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...getChartTooltipStyle(dark),
        // Keep the tall readout away from the hovered (clickable) lines.
        position: 'cursorAway',
        itemSort: (a, b) => a.parsed.y - b.parsed.y,
        callbacks: {
          label: (ctx) => {
            if (ctx.parsed.y == null) return null;
            const p = ctx.dataset.pointMeta?.[ctx.dataIndex];
            const base = `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)}`;
            if (!p) return base;
            // Under-covered vs the ensemble: show the fraction so the
            // reader sees the comparison caveat, not just a raw count.
            if (p.ens_n && p.n_samples < COVERAGE_THRESHOLD * p.ens_n) {
              const pct = Math.round((p.n_samples / p.ens_n) * 100);
              return `${base} (n=${p.n_samples}/${p.ens_n}, ${pct}% coverage)`;
            }
            return `${base} (${p.n_days}d, n=${p.n_samples})`;
          },
          // How many lines the per-tick readout was truncated by.
          footer: (items) => {
            if (traceActive || !items.length) return '';
            const idx = items[0].dataIndex;
            const total = chartData.datasets.filter(
              (d) => d.data[idx] != null,
            ).length;
            const hidden = total - items.length;
            return hidden > 0 ? `+${hidden} more (click a line to spotlight)` : '';
          },
        },
        // With a trace active, read out only the focused forecaster and the
        // ensemble. Otherwise cap the per-tick list to the best performers
        // plus the ensemble so the tooltip fits inside the canvas.
        filter: (item, _index, items) => {
          if (item.parsed.y == null) return false;
          const m = item.dataset.meta;
          if (traceActive) {
            return m?.isEnsemble || m?.userId === traceUserId;
          }
          if (m?.isEnsemble) return true;
          // Rank by value with datasetIndex as tiebreaker so tied values
          // cannot all pass a plain cutoff and overflow the canvas.
          const idx = item.dataIndex;
          const ranked = items
            .filter((it) => (
              it.parsed.y != null && it.dataIndex === idx
                && !it.dataset.meta?.isEnsemble
            ))
            .sort((a, b) => (
              (a.parsed.y - b.parsed.y) || (a.datasetIndex - b.datasetIndex)
            ));
          const rank = ranked.findIndex(
            (it) => it.datasetIndex === item.datasetIndex,
          );
          return rank > -1 && rank < TOOLTIP_MAX_ROWS;
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: TEXT_COLOR(dark),
          maxRotation: 0,
          autoSkip: true,
          font: { size: 10 },
        },
        title: {
          display: true,
          text: 'Lead time (hours from gate closure)',
          color: TEXT_COLOR(dark),
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: false,
        grid: { color: GRID_COLOR(dark) },
        ticks: { color: TEXT_COLOR(dark), font: { size: 10 } },
        title: {
          display: true,
          text: `${metricLabel} error`,
          color: TEXT_COLOR(dark),
          font: { size: 11 },
        },
      },
    },
  }), [dark, metricLabel, traceActive, traceUserId, onTrace, chartData]);

  return (
    <section>
      {/* Gap to the card lives on the title block (pb-3), not the header,
          so a taller right-side stat block cannot stretch it. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 pb-3">
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
            Lead time
          </div>
          <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            Error by forecast lead time
          </h3>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 flex-shrink-0 mt-1">
          {headerRight}
          {selectedForecaster && onHideUser && (
            <button
              type="button"
              onClick={() => onHideUser(selectedForecaster.meta.userId)}
              title={`Hide ${selectedForecaster.label} from both error plots`}
              className="max-w-[220px] truncate rounded-full border border-gray-300 bg-surface px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-300 dark:hover:border-brand-500/60 dark:hover:bg-brand-500/10 dark:hover:text-brand-300"
            >
              Hide {selectedForecaster.label}
            </button>
          )}
          {hiddenUserIds.length > 0 && (
            <button
              type="button"
              onClick={onRestoreHidden}
              className="rounded-full border border-brand-300 bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 whitespace-nowrap transition-colors hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-brand-500/60 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
            >
              <span className="font-mono tabular-nums">{hiddenUserIds.length}</span>
              {' hidden · Restore'}
            </button>
          )}
          <button
            type="button"
            aria-pressed={fullCoverageOnly}
            onClick={() => setFullCoverageOnly((v) => !v)}
            title={`Hide forecasters with less than ${Math.round(COVERAGE_THRESHOLD * 100)}% of the ensemble's sample coverage`}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
              fullCoverageOnly
                ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/60 dark:bg-brand-500/10 dark:text-brand-300'
                : 'border-gray-200/70 text-slate-500 hover:bg-slate-100/70 dark:border-gray-600/70 dark:text-gray-400 dark:hover:bg-gray-800/60'
            }`}
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                fullCoverageOnly
                  ? 'bg-brand-600 dark:bg-brand-400'
                  : 'border border-slate-400 dark:border-gray-500'
              }`}
            />
            Comparable only
            {lowCoverageCount > 0 && (
              <span className="tabular-nums opacity-70">
                {fullCoverageOnly ? `${lowCoverageCount} hidden` : lowCoverageCount}
              </span>
            )}
          </button>
        </div>
      </header>
      <div className="bg-surface dark:bg-gray-870 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
        <div className="p-5">
          {!hasData ? (
            <div className="h-48 rounded-md flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              No live-submission leadtime data for this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ height: 300, minWidth: 480 }}>
                <Line data={chartData} options={options} />
              </div>
            </div>
          )}
          <p className="mt-3 pl-[30px] text-[11px] text-slate-500 dark:text-gray-400">
            Live submissions only; forward-filled slots are excluded. Hollow
            points mark coverage below {Math.round(COVERAGE_THRESHOLD * 100)}%
            of the ensemble&apos;s sample count at that lead time (error not
            directly comparable); hover for exact counts. Teal is the Predico
            ensemble.
            {' Select a forecaster, then use the Hide action above to remove it from both error plots.'}
            {fullCoverageOnly && lowCoverageCount > 0
              ? ` ${lowCoverageCount} under-covered forecaster${lowCoverageCount === 1 ? '' : 's'} hidden.`
              : ''}
          </p>
        </div>
      </div>
    </section>
  );
}
